const DISALLOWED_TEXT_PATTERN = /[<>{}[\];$`\\]/

/**
 * Sanitizes general user-provided text intended for SQL-backed storage.
 * Collapses repeated whitespace, enforces a conservative maximum length,
 * and rejects characters that are commonly used in injection payloads.
 *
 * @param {string} raw - Raw user provided text.
 * @param {{ maxLength?: number }} [options]
 * @returns {{ sanitized: string, error?: string }}
 */
export function sanitizeUserTextInput(raw, { maxLength = 120 } = {}) {
  if (typeof raw !== "string") {
    return { sanitized: "", error: "Invalid input." }
  }

  const collapsed = raw.trim().replace(/\s+/g, " ")
  if (!collapsed) {
    return { sanitized: "", error: "Value cannot be empty." }
  }

  if (collapsed.length > maxLength) {
    return {
      sanitized: "",
      error: `Value must be ${maxLength} characters or fewer.`,
    }
  }

  if (DISALLOWED_TEXT_PATTERN.test(collapsed)) {
    return {
      sanitized: "",
      error:
        "Value contains unsupported characters (<, >, {, }, [, ], ;, $, `, \\).",
    }
  }

  return { sanitized: collapsed }
}

/**
 * Normalizes URLs for duplicate detection and storage.
 * Mirrors the previous normalization logic used in WebsiteScraping.
 *
 * @param {string} rawUrl
 * @returns {string | null}
 */
export function normalizeUrl(rawUrl) {
  if (!rawUrl) return null
  let url = rawUrl.trim().toLowerCase()
  if (!/^https?:\/\//.test(url)) url = "http://" + url

  try {
    const parsed = new URL(url)
    const host = parsed.hostname
    let path = parsed.pathname
    if (path.endsWith("/")) path = path.slice(0, -1)
    return `https://${host}${path}${parsed.search}`
  } catch {
    return null
  }
}
